import React, { useEffect, useState } from 'react';
import { Loader2, History } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const fmtDT = (s) => s
  ? new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

export const DispatchLogDialog = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/invoices/dispatch-log')
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-sky-600" /> Versandprotokoll Rechnungen
          </DialogTitle>
        </DialogHeader>
        {loading || !data ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Lade…</div>
        ) : (
          <Tabs defaultValue="emails">
            <TabsList>
              <TabsTrigger value="emails" data-testid="dispatch-log-tab-emails">E-Mail-Versand ({data.emails.length})</TabsTrigger>
              <TabsTrigger value="runs" data-testid="dispatch-log-tab-runs">Automatische Läufe ({data.runs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="emails" className="mt-4">
              {data.emails.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-6 text-center">Noch keine Rechnungs-E-Mails versendet.</p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="dispatch-log-emails-table">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Zeitpunkt</th>
                        <th className="text-left px-3 py-2">Rechnung</th>
                        <th className="text-left px-3 py-2">Empfänger</th>
                        <th className="text-left px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.emails.map((e, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDT(e.at)}</td>
                          <td className="px-3 py-1.5 font-mono">{e.invoice_number}</td>
                          <td className="px-3 py-1.5">{e.recipient}</td>
                          <td className="px-3 py-1.5">
                            {e.status === 'sent'
                              ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]">Gesendet</Badge>
                              : <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-600 text-[10px]" title={e.error || ''}>Fehlgeschlagen</Badge>}
                            {e.status !== 'sent' && e.error && (
                              <div className="text-[10px] text-rose-500 mt-0.5 max-w-[280px] truncate" title={e.error}>{e.error}</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="runs" className="mt-4">
              {data.runs.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-6 text-center">
                  Noch keine automatischen Läufe. Der Versand-Job läuft täglich zwischen 8 und 9 Uhr
                  und versendet zu Periodenbeginn (je nach Turnus des Mandanten).
                </p>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="dispatch-log-runs-table">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Zeitpunkt</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Ergebnis je Mandant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.runs.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100 align-top">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDT(r.started_at)}</td>
                          <td className="px-3 py-1.5">
                            {r.status === 'success'
                              ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]">OK</Badge>
                              : <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-600 text-[10px]" title={r.error || ''}>Fehler</Badge>}
                          </td>
                          <td className="px-3 py-1.5">
                            {(r.result?.details || []).length === 0
                              ? <span className="text-slate-400 italic">Kein Mandant fällig ({r.result?.tenants_checked ?? 0} geprüft)</span>
                              : r.result.details.map((d, j) => (
                                  <div key={j} className="mb-0.5">
                                    <span className="font-mono">{d.tenant}</span>: {d.sent} gesendet
                                    {d.failed > 0 && <span className="text-rose-600">, {d.failed} fehlgeschlagen</span>}
                                    {d.no_email > 0 && <span className="text-brand-red">, {d.no_email} ohne E-Mail</span>}
                                  </div>
                                ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DispatchLogDialog;
